/** Nested scroller for public `/website/...` pages (not `window`). */
export const MASTER_TEMPLATE_SCROLL_ROOT_ID = "master-template-scroll-root";

/**
 * Public LiB sets `overflow-y: auto` on `#master-template-scroll-root` but the
 * readonly shell grows with the page, so the window scrolls. Listening on that
 * element never fires. Only treat it as the scroller when it actually overflows.
 */
function isActiveScrollContainer(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight + 2) return false;
  const oy = window.getComputedStyle(el).overflowY;
  return oy === "auto" || oy === "scroll" || oy === "overlay";
}

function pickScrollElement(id: string): HTMLElement | null {
  const el = document.getElementById(id);
  if (!el || !isActiveScrollContainer(el)) return null;
  return el;
}

/**
 * Prefer an explicit scroll container (website template), then fall back to
 * `window` for classic landing / preview pages and public shells that grow.
 */
export function resolveFeedScrollRoot(
  preferredId?: string | null
): HTMLElement | Window {
  if (typeof document === "undefined") return window;
  const preferred = String(preferredId || "").trim();
  if (preferred) {
    const el = pickScrollElement(preferred);
    if (el) return el;
  }
  const templateRoot = pickScrollElement(MASTER_TEMPLATE_SCROLL_ROOT_ID);
  if (templateRoot) return templateRoot;
  return window;
}

/** Id to pass to `react-infinite-scroll-component` `scrollableTarget`, or undefined for window. */
export function resolveFeedScrollableTargetId(
  preferredId?: string | null
): string | undefined {
  const root = resolveFeedScrollRoot(preferredId);
  if (root instanceof HTMLElement && root.id) return root.id;
  return undefined;
}

export function getFeedScrollMetrics(preferredId?: string | null): {
  scrollTop: number;
  maxScroll: number;
  scrollPercentage: number;
} {
  const root = resolveFeedScrollRoot(preferredId);
  if (root === window || !(root instanceof HTMLElement)) {
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || 0;
    const maxScroll = Math.max(
      0,
      document.documentElement.scrollHeight -
        document.documentElement.clientHeight
    );
    return {
      scrollTop,
      maxScroll,
      scrollPercentage: maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 100,
    };
  }
  const scrollTop = root.scrollTop || 0;
  const maxScroll = Math.max(0, root.scrollHeight - root.clientHeight);
  return {
    scrollTop,
    maxScroll,
    scrollPercentage: maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 100,
  };
}

/**
 * `html { scroll-behavior: smooth }` turns `scrollTo(0, 0)` into a glide, so a
 * new category still paints at the click position. Force an instant jump, then
 * restore the inline style after the scroll is committed.
 */
function runInstantScroll(scroller: HTMLElement, run: () => void) {
  const prevBehavior = scroller.style.scrollBehavior;
  const prevAnchor = scroller.style.overflowAnchor;
  scroller.style.scrollBehavior = "auto";
  // A shorter skeleton would otherwise anchor the viewport back to the click.
  scroller.style.overflowAnchor = "none";
  const apply = () => {
    scroller.style.scrollBehavior = "auto";
    run();
  };
  apply();
  // Chrome applies scroll-behavior at animation start, which can be the frame
  // after we would have restored `smooth`. Re-apply the jump while `auto` is
  // still set, then restore.
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(() => {
      apply();
      if (scroller.style.scrollBehavior === "auto") {
        scroller.style.scrollBehavior = prevBehavior;
      }
      scroller.style.overflowAnchor = prevAnchor;
    });
  });
}

/** Move the public feed (window or template root) to the top of the page. */
export function scrollFeedToTop(preferredId?: string | null) {
  const root = resolveFeedScrollRoot(preferredId);
  if (root === window || !(root instanceof HTMLElement)) {
    runInstantScroll(document.documentElement, () => {
      document.documentElement.scrollTop = 0;
      window.scrollTo(0, 0);
    });
    return;
  }
  runInstantScroll(root, () => {
    root.scrollTop = 0;
  });
}

/** Put an element at the top of the feed scroller (opened look / outfit). */
export function scrollElementToFeedTop(
  el: HTMLElement,
  preferredId?: string | null,
) {
  const root = resolveFeedScrollRoot(preferredId);
  const top = el.getBoundingClientRect().top;
  if (root === window || !(root instanceof HTMLElement)) {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const next = Math.max(0, y + top);
    runInstantScroll(document.documentElement, () => {
      window.scrollTo(0, next);
    });
    return;
  }
  const origin = root.getBoundingClientRect().top;
  runInstantScroll(root, () => {
    root.scrollTop += top - origin;
  });
}

export function addFeedScrollListener(
  handler: EventListenerOrEventListenerObject,
  preferredId?: string | null
): () => void {
  const root = resolveFeedScrollRoot(preferredId);
  root.addEventListener("scroll", handler, { passive: true });
  return () => root.removeEventListener("scroll", handler);
}
